/**
 * Keyboard rules for the app's own dialogs — the command palette, the
 * queue, the browser and file windows, the history overlay — that are not a
 * HeroUI `Modal` (React Aria gives a Modal all of this already). Roadmap U9:
 *
 *   - focus moves inside when the dialog opens (a named element, or the
 *     first thing that takes focus, or the dialog itself);
 *   - Escape closes it — but not while a text field has the caret, where
 *     Escape belongs to the field (a rename, an address being typed);
 *   - a modal dialog keeps Tab inside itself, wrapping at either end;
 *   - closing hands focus back to whatever had it before.
 *
 * The decisions are pure functions here (test/dialogFocus.test.ts); the
 * DOM side is `hooks/useDialogFocus.ts`.
 */

/** What a keydown means to an open dialog. */
export type DialogKeyAction = 'close' | 'wrap-to-first' | 'wrap-to-last' | 'none';

export interface DialogKeyContext {
  key: string;
  shiftKey: boolean;
  /** Tag of the element the event came from (`INPUT`, `TEXTAREA`, …). */
  targetTag?: string;
  /** Whether that element is `contenteditable` (an editor surface). */
  targetEditable?: boolean;
  /** Where focus sits among the dialog's focusable elements (-1: on none of them). */
  index: number;
  count: number;
  /** Escape closes this dialog (false: the caller handles Escape itself). */
  closeOnEscape: boolean;
  /** Tab stays inside (a modal dialog). */
  trap: boolean;
  /** Escape closes even from inside a text field (the palette's own search box). */
  escapeFromFields?: boolean;
}

const FIELD_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/** Whether Escape here belongs to a text field rather than to the dialog. */
export function escapeBelongsToField(targetTag: string | undefined, editable = false): boolean {
  return editable || (!!targetTag && FIELD_TAGS.has(targetTag.toUpperCase()));
}

export function dialogKeyAction(ctx: DialogKeyContext): DialogKeyAction {
  if (ctx.key === 'Escape') {
    if (!ctx.closeOnEscape) return 'none';
    if (!ctx.escapeFromFields && escapeBelongsToField(ctx.targetTag, ctx.targetEditable)) return 'none';
    return 'close';
  }
  if (ctx.key === 'Tab' && ctx.trap) {
    if (ctx.count === 0) return 'none';
    // Focus has left the list (on the dialog itself, or somewhere it should not be): bring it back in.
    if (ctx.index < 0) return ctx.shiftKey ? 'wrap-to-last' : 'wrap-to-first';
    if (!ctx.shiftKey && ctx.index === ctx.count - 1) return 'wrap-to-first';
    if (ctx.shiftKey && ctx.index === 0) return 'wrap-to-last';
  }
  return 'none';
}

/** What a dialog focuses when it opens. */
export function initialFocusChoice(opts: { alreadyInside: boolean; hasPreferred: boolean; focusableCount: number }): 'keep' | 'preferred' | 'first' | 'container' {
  if (opts.alreadyInside) return 'keep';
  if (opts.hasPreferred) return 'preferred';
  return opts.focusableCount > 0 ? 'first' : 'container';
}

/** The elements Tab can reach, as a selector (filtered for visibility and `disabled` by the hook). */
export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');
